import React, { createContext, useCallback, useContext, useEffect, useMemo, useState } from 'react';

import { ICollaboratorBenefit } from '../../interfaces/businessData';
import { ICompanyBenefit, ICompanyPlan, IPlansContext } from '../../interfaces/plans';
import Company from '../../services/api/companies/companies';
import { CompanyContext } from '../company/companyContext';

const contextDefaultValues: IPlansContext = {
  plans: [],
  loading: false,
  error: false,
  selectedBenefit: undefined,
  bfuChoices: undefined,
  selectedPlan: undefined,
  planType: '',
  modelType: '',
  fetchPlans: () => {
    return;
  },
  assingSelectedBenefit: () => {
    return;
  },
  assingBfuChoices: () => {
    return;
  },
  assingSelectedPlan: () => {
    return;
  }
};

export const PlansContext = createContext<IPlansContext>(contextDefaultValues);

const PlansContextProvider = ({ children }: React.PropsWithChildren) => {
  const [plans, setPlans] = useState<ICompanyPlan[]>(contextDefaultValues.plans);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(false);
  const [bfuChoices, setBfuChoices] = useState<number>();
  const [selectedBenefit, setSelectedBenefit] = useState<ICompanyBenefit | ICollaboratorBenefit>();
  const [selectedPlan, setSelectedPlan] = useState<ICompanyPlan>();
  const [planType, setPlanType] = useState<string>('');
  const [modelType, setModelType] = useState<string>('');

  const { company, locale, handleCompanyPlansType } = useContext(CompanyContext);

  const fetchPlans = useCallback(async () => {
    try {
      if (!company.id || !locale) return;
      setLoading(true);
      setError(false);
      setPlans([]);
      setSelectedPlan(undefined);
      handleCompanyPlansType('');
      setPlanType('');
      setModelType('');
      const response = await Company.getCompanyPlans(company.id, locale);
      setPlans(response.plans);
      setSelectedPlan(response.plans[0]);
      handleCompanyPlansType(response.type);
      setPlanType(response.type);
      setModelType(response.modelType || '');
    } catch {
      setError(true);
      setPlans([]);
    } finally {
      setLoading(false);
    }
  }, [company, locale, setSelectedPlan, handleCompanyPlansType]);

  useEffect(() => {
    fetchPlans();
  }, [fetchPlans]);

  const assingBfuChoices = useCallback((choices: number) => {
    setBfuChoices(choices);
  }, []);

  const assingSelectedBenefit = useCallback((benefit: ICompanyBenefit) => {
    setSelectedBenefit(benefit);
  }, []);

  const assingSelectedPlan = useCallback((plan: ICompanyPlan | undefined) => {
    setSelectedPlan(plan);
  }, []);

  const value = useMemo(
    () => ({
      plans,
      loading,
      error,
      bfuChoices,
      selectedBenefit,
      selectedPlan,
      planType,
      modelType,
      fetchPlans,
      assingBfuChoices,
      assingSelectedBenefit,
      assingSelectedPlan
    }),
    [
      plans,
      loading,
      error,
      bfuChoices,
      selectedBenefit,
      selectedPlan,
      planType,
      modelType,
      fetchPlans,
      assingBfuChoices,
      assingSelectedBenefit,
      assingSelectedPlan
    ]
  );

  return <PlansContext.Provider value={value}>{children}</PlansContext.Provider>;
};

export default PlansContextProvider;
